import { useEffect, useState, useCallback } from 'react'
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel,
  Button, Stack, CircularProgress, Alert, TablePagination, TextField, Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import FilterListIcon from '@mui/icons-material/FilterList'
import { fetchDataTable, toDDMMYYYY } from '../api/datatable'

const getPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj)

// Grid SERVER-SIDE (endpoint DataTables de Qualita): paginación + filtro por columna + orden
// en el backend (solo trae la página). Reemplaza el patrón view=BaseList que traía todo.
// columns: [{ data, label, type='string'|'date-range'|'number-range'|'combo', align, render, sortable, filterable }]
export default function ServerDataGrid({
  title, resource, view, baseFilter = null, columns,
  defaultOrderDir = 'desc', pageSize = 10, rowActions, headerExtra,
}) {
  // Se antepone 'id' como columna 0 (orden por defecto); las visibles arrancan en índice 1.
  const dtColumns = [{ data: 'id', type: 'string' }, ...columns.map((c) => ({ data: c.data, type: c.type || 'string' }))]

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [length, setLength] = useState(pageSize)
  const [order, setOrder] = useState({ column: 0, dir: defaultOrderDir })
  const [showFilters, setShowFilters] = useState(false)
  const [colSearch, setColSearch] = useState({})
  const [dateRange, setDateRange] = useState({})

  const baseKey = JSON.stringify(baseFilter)
  const searchKey = JSON.stringify(colSearch)

  const cargar = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await fetchDataTable(resource, {
        view, baseFilter, columns: dtColumns, start: page * length, length,
        orderColumn: order.column, orderDir: order.dir, columnSearch: colSearch,
      })
      setRows(r.rows); setTotal(r.recordsFiltered)
    } catch (e) {
      setError('No se pudo cargar la tabla.'); setRows([]); setTotal(0)
    } finally { setLoading(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, view, baseKey, page, length, order.column, order.dir, searchKey])

  useEffect(() => {
    const t = setTimeout(cargar, 350) // debounce de filtros
    return () => clearTimeout(t)
  }, [cargar])

  const setFilter = (data, value) => { setColSearch((s) => ({ ...s, [data]: value })); setPage(0) }
  const setDateFilter = (data, which, value) => {
    setDateRange((dr) => {
      const next = { ...(dr[data] || {}), [which]: value }
      const desde = next.desde || next.hasta
      const hasta = next.hasta || next.desde
      const combined = desde && hasta ? `${toDDMMYYYY(desde)}~${toDDMMYYYY(hasta)}` : ''
      setColSearch((s) => ({ ...s, [data]: combined }))
      return { ...dr, [data]: next }
    })
    setPage(0)
  }
  const sortBy = (j) => {
    const dtIdx = j + 1
    setOrder((o) => ({ column: dtIdx, dir: o.column === dtIdx && o.dir === 'asc' ? 'desc' : 'asc' }))
    setPage(0)
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight={600}>{title}</Typography>
        {headerExtra}
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={cargar} disabled={loading}>Actualizar</Button>
          <Tooltip title="Filtrar por columna">
            <Button variant={showFilters ? 'contained' : 'outlined'} color={showFilters ? 'secondary' : 'inherit'}
              startIcon={<FilterListIcon />}
              onClick={() => { if (showFilters) { setColSearch({}); setDateRange({}) } setShowFilters((s) => !s) }}>
              Filtros
            </Button>
          </Tooltip>
          {loading && <CircularProgress size={20} />}
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">{total} registros</Typography>
        </Stack>
      </Paper>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600 } }}>
              {columns.map((c, j) => (
                <TableCell key={c.data} align={c.align}>
                  {c.sortable === false ? c.label : (
                    <TableSortLabel active={order.column === j + 1} direction={order.column === j + 1 ? order.dir : 'asc'} onClick={() => sortBy(j)}>
                      {c.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
              {rowActions && <TableCell align="center" width={90}>Acciones</TableCell>}
            </TableRow>
            {showFilters && (
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.data} sx={{ py: 0.5 }}>
                    {c.filterable === false ? null : c.type === 'date-range' ? (
                      <Stack direction="row" spacing={0.5}>
                        <TextField type="date" variant="standard" size="small" sx={{ width: 130 }} label="Desde" InputLabelProps={{ shrink: true }}
                          value={dateRange[c.data]?.desde || ''} onChange={(e) => setDateFilter(c.data, 'desde', e.target.value)} />
                        <TextField type="date" variant="standard" size="small" sx={{ width: 130 }} label="Hasta" InputLabelProps={{ shrink: true }}
                          value={dateRange[c.data]?.hasta || ''} onChange={(e) => setDateFilter(c.data, 'hasta', e.target.value)} />
                      </Stack>
                    ) : (
                      <TextField variant="standard" size="small" fullWidth placeholder="Filtrar…"
                        value={colSearch[c.data] || ''} onChange={(e) => setFilter(c.data, e.target.value)} />
                    )}
                  </TableCell>
                ))}
                {rowActions && <TableCell />}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow><TableCell colSpan={columns.length + (rowActions ? 1 : 0)} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin registros.</TableCell></TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                {columns.map((c) => <TableCell key={c.data} align={c.align}>{c.render ? c.render(row) : (getPath(row, c.data) ?? '—')}</TableCell>)}
                {rowActions && <TableCell align="center">{rowActions(row)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={length} onRowsPerPageChange={(e) => { setLength(parseInt(e.target.value, 10)); setPage(0) }}
          rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`} />
      </Paper>
    </Box>
  )
}
